// Thin GraphQL client wrapper.
//
// Usage:
//   const data = await epicQuery<MyType>(MY_QUERY, { foo: "bar" }, token, refresh);
//
// All queries hit one endpoint (EPIC_GRAPHQL_URL) and authenticate via the
// existing Microsoft Bearer JWT — same token Laravel's azuretoken middleware
// already validates for the REST routes.
//
// `refresh` is an optional callback that returns a new access token. When
// present and the request comes back 401, we call refresh() and retry once
// — handles the common case of a stale cached token without surfacing the
// error to the UI.

import { GraphQLClient } from "graphql-request";
import { EPIC_GRAPHQL_URL } from "../../auth/config";

export type TokenRefresh = () => Promise<string>;

export function createEpicClient(accessToken: string): GraphQLClient {
  return new GraphQLClient(EPIC_GRAPHQL_URL, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

export async function epicQuery<T>(
  query: string,
  variables: Record<string, unknown> | undefined,
  accessToken: string,
  refresh?: TokenRefresh
): Promise<T> {
  try {
    return await createEpicClient(accessToken).request<T>(query, variables);
  } catch (e: any) {
    if (refresh && e?.response?.status === 401) {
      const newToken = await refresh();
      return await createEpicClient(newToken).request<T>(query, variables);
    }
    throw e;
  }
}
